import { Draggable } from '@hello-pangea/dnd';
import { makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import { memo, useCallback, useRef } from 'react';
import { COLORS } from '../../../theme/variables';
import { TooltipData } from './heatmap-tooltip';
import { getScoreColor, getScoreTextColor } from './utils';

export interface ChipData {
  chipId: string;
  /** Actual backend recommendation ID (may differ from chipId in mentor view) */
  recommendationId?: string;
  name: string;
  score: number;
  assigned: boolean;
  tooltipData: TooltipData;
  locked?: boolean;
}

interface HeatmapChipProps {
  chipId: string;
  index: number;
  name: string;
  score: number;
  assigned: boolean;
  onRemove: (chipId: string) => void;
  onHover: (el: HTMLElement | null, data: TooltipData | null) => void;
  tooltipData: TooltipData;
  hideScore?: boolean;
  locked?: boolean;
}

const useStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 16,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'grab',
    userSelect: 'none',
    transition: 'opacity 0.12s',
  },
  chipDefault: {
    border: '2px solid transparent',
  },
  chipLocked: {
    border: '2px solid transparent',
    cursor: 'default',
  },
  checkIcon: {
    fontSize: 16,
    color: COLORS.COLOR_GREEN_BASE,
    flexShrink: 0,
  },
  nameText: {
    width: '21ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  scoreBadge: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 10,
    padding: '1px 6px',
    flexShrink: 0,
  },
  removeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    flexShrink: 0,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    '&:hover': {
      color: COLORS.COLOR_RED_BASE,
    },
  },
  removeIcon: {
    fontSize: 14,
  },
});

const HeatmapChip = memo(function HeatmapChip({
  chipId,
  index,
  name,
  score,
  assigned,
  onRemove,
  onHover,
  tooltipData,
  hideScore,
  locked,
}: HeatmapChipProps) {
  const classes = useStyles();
  const chipRef = useRef<HTMLDivElement>(null);

  const bgColor = getScoreColor(score);
  const textColor = getScoreTextColor(score);

  const handleMouseEnter = useCallback(() => {
    if (chipRef.current) {
      onHover(chipRef.current, tooltipData);
    }
  }, [onHover, tooltipData]);

  const handleMouseLeave = useCallback(() => {
    onHover(null, null);
  }, [onHover]);

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(chipId);
    },
    [onRemove, chipId],
  );

  const chipClass = locked ? classes.chipLocked : classes.chipDefault;

  return (
    <Draggable draggableId={chipId} index={index} isDragDisabled={!!locked}>
      {(provided, snapshot) => (
        <div
          ref={(el) => {
            provided.innerRef(el);
            (chipRef as React.MutableRefObject<HTMLDivElement | null>).current =
              el;
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${classes.chip} ${chipClass}`}
          style={{
            ...provided.draggableProps.style,
            backgroundColor: bgColor,
            color: textColor,
            opacity: snapshot.isDragging ? 0.6 : 1,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          aria-label={`${name} – score ${score}`}
          data-testid={`heatmap-chip-${chipId}`}
        >
          {(assigned || locked) && (
            <CheckCircleIcon
              className={classes.checkIcon}
            />
          )}
          <span className={classes.nameText}>{name}</span>
          {!hideScore && (
            <span
              className={classes.scoreBadge}
              style={{
                backgroundColor: 'rgba(255,255,255,0.3)',
                color: textColor,
              }}
            >
              {score}
            </span>
          )}
          {!locked && (
            <button
              className={classes.removeBtn}
              onClick={handleRemove}
              aria-label={`Remove ${name}`}
              data-testid={`remove-chip-${chipId}`}
              type='button'
            >
              <CloseIcon
                className={classes.removeIcon}
                style={{ color: textColor }}
              />
            </button>
          )}
        </div>
      )}
    </Draggable>
  );
});

export default HeatmapChip;
